import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { WebSocketProvider } from "@/context/WebSocketContext";
import { LanguageProvider } from "@/context/LanguageContext";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import DevicesPage from "@/pages/DevicesPage";
import InstallStatusPage from "@/pages/InstallStatusPage";
import DeviceDetailPage from "@/pages/DeviceDetailPage";
import UsersPage from "@/pages/UsersPage";
import TenantsPage from "@/pages/TenantsPage";
import TenantDetailPage from "@/pages/TenantDetailPage";
import GroupsPage from "@/pages/GroupsPage";
import ScriptsPage from "@/pages/ScriptsPage";
import MapPage from "@/pages/MapPage";
import AuditLogPage from "@/pages/AuditLogPage";
import FileManagerPage from "@/pages/FileManagerPage";
import AgentUpdatePage from "@/pages/AgentUpdatePage";
import DeploymentsPage from "@/pages/DeploymentsPage";
import HealthCenterPage from "@/pages/HealthCenterPage";
import DistributionPage from "@/pages/DistributionPage";
import KioskPage from "@/pages/KioskPage";
import UploadsPage from "@/pages/UploadsPage";
import Layout from "@/components/Layout";

const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center"><div className="spinner" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
};

function AppRoutes() {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <LoginPage />} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="/dashboard" element={<ProtectedRoute><Layout><DashboardPage /></Layout></ProtectedRoute>} />
      <Route path="/devices" element={<ProtectedRoute><Layout><DevicesPage /></Layout></ProtectedRoute>} />
      <Route path="/devices/:deviceId" element={<ProtectedRoute><Layout><DeviceDetailPage /></Layout></ProtectedRoute>} />
      <Route path="/map" element={<ProtectedRoute><Layout><MapPage /></Layout></ProtectedRoute>} />
      <Route path="/groups" element={<ProtectedRoute><Layout><GroupsPage /></Layout></ProtectedRoute>} />
      <Route path="/scripts" element={<ProtectedRoute allowedRoles={['superadmin', 'admin']}><Layout><ScriptsPage /></Layout></ProtectedRoute>} />
      <Route path="/install-status" element={<ProtectedRoute allowedRoles={['superadmin', 'admin']}><Layout><InstallStatusPage /></Layout></ProtectedRoute>} />
      <Route path="/audit-log" element={<ProtectedRoute allowedRoles={['superadmin', 'admin']}><Layout><AuditLogPage /></Layout></ProtectedRoute>} />
      <Route path="/users" element={<ProtectedRoute allowedRoles={['superadmin', 'admin']}><Layout><UsersPage /></Layout></ProtectedRoute>} />
      <Route path="/tenants" element={<ProtectedRoute allowedRoles={['superadmin']}><Layout><TenantsPage /></Layout></ProtectedRoute>} />
      <Route path="/agent-update" element={<ProtectedRoute><Layout><AgentUpdatePage /></Layout></ProtectedRoute>} />
      <Route path="/deployments" element={<ProtectedRoute allowedRoles={['superadmin', 'admin']}><Layout><DeploymentsPage /></Layout></ProtectedRoute>} />
      <Route path="/filemanager" element={<ProtectedRoute><Layout><FileManagerPage /></Layout></ProtectedRoute>} />
      <Route path="/filemanager/:deviceId" element={<ProtectedRoute><Layout><FileManagerPage /></Layout></ProtectedRoute>} />
      <Route path="/tenants/:tenantId" element={<ProtectedRoute allowedRoles={['superadmin']}><Layout><TenantDetailPage /></Layout></ProtectedRoute>} />
      <Route path="/health" element={<ProtectedRoute allowedRoles={["superadmin", "admin"]}><Layout><HealthCenterPage /></Layout></ProtectedRoute>} />
      <Route path="/distribution" element={<ProtectedRoute><Layout><DistributionPage /></Layout></ProtectedRoute>} />
      <Route path="/kiosk" element={<ProtectedRoute><Layout><KioskPage /></Layout></ProtectedRoute>} />
      <Route path="/uploads" element={<ProtectedRoute><Layout><UploadsPage /></Layout></ProtectedRoute>} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <WebSocketProvider>
            <AppRoutes />
            <Toaster position="top-right" toastOptions={{ style: { background: '#141416', border: '1px solid rgba(255, 255, 255, 0.1)', color: '#fff' } }} />
          </WebSocketProvider>
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
